import React from 'react';
import { nanoid } from 'nanoid';
import type { WidgetConfig } from '../hooks/useFetchWidgetConfig';
import { getIsMobile } from '../utils/device';
import { getClientContext, type ClientContext } from '../utils/visitor-context';
import { getWidgetVersion } from '../utils/widget-version';
import { getMetadataSnapshot } from '../sdk/metadata-store';
import { mergeMclidMetadata } from '../sdk/mclid';
import {
  trackWidgetEvent,
  updateWidgetAnalyticsSessionId,
} from '../utils/analytics';
import {
  getOrSetSessionId,
  getOrSetThreadId,
  persistSessionId,
  persistThreadId,
  lastMessageAtStorageKey,
  readLastMessageAt,
  writeLastMessageAt,
  clearLastMessageAt,
  isSessionInactiveExpired,
  getInactiveForBucket,
} from '../utils/sessionIdentity';
import {
  uploadVoiceRecording,
  VoiceUploadError,
} from '../utils/uploadVoiceRecording';
import {
  transcribeVoiceRecording,
  VoiceTranscribeError,
} from '../utils/transcribeVoiceRecording';
import {
  CHAT_WIDGET_PANEL_WIDTH_KEY,
  PANEL_WIDTH_DEFAULT,
} from '@/lib/panel-resize';
import { usePersistedPanelWidth } from '@/lib/usePersistedPanelWidth';
import type { ChatSurface } from '@/lib/chat-surface';
import { anamClient } from '../avatar/anamClient';
import {
  AvatarSessionProvider,
  useAvatarSession,
} from '../avatar/useAvatarSession';
import {
  createAvatarSpeechOrchestrator,
  type AvatarSpeechOrchestrator,
} from '../avatar/avatarSpeechOrchestrator';
import { isIntakeCompleted, markIntakeCompleted } from './intake-form';

export interface ChatMessage {
  text: string;
  timestamp: number;
  role: 'user' | 'bot';
  messageId?: string;
  feedback?: {
    rating: 'up' | 'down' | null;
    comment?: string;
  };
}

/**
 * Categorisation surfaced to the widget UI so it can pick the right notice
 * (mic blocked vs. transcription failed) and copy. `null` means there is
 * no active voice error — the most recent voice send succeeded, was
 * dismissed, or no voice message has been attempted.
 */
export type VoiceErrorReason = 'transcribe_failed' | 'upload_failed';

export interface VoiceErrorState {
  reason: VoiceErrorReason;
  /** Cached blob so the user can retry without re-recording. */
  blob: Blob;
  /**
   * Result of a successful upload that has not yet been transcribed (or was
   * transcribed but the request failed). When present, retry can skip the
   * S3 upload and call the transcribe endpoint directly with these values,
   * which saves bandwidth + S3 cost since the object is still alive in the
   * 24-hour bucket lifecycle window.
   */
  uploaded?: {
    audioUrl: string;
    mediaKey: string;
  };
}

export type ChatLayout = 'docked' | 'fullscreen' | 'minimized';

interface ChatState {
  open: boolean;
  state: 'idle' | 'pending';
  layout: ChatLayout;
  dockModeEnabled: boolean;
  panelWidth: number;
  setPanelWidth: (width: number) => void;
  history: ChatMessage[];
  threadId: string;
  sessionId: string;
  engageId: string;
  isServiceDown: boolean; // True after 3 consecutive message failures
  /** SEM-7139 — institution requires intake before chat */
  mandatoryIntakeOnOpen: boolean;
  /** True after successful intake submit (or previously completed for session) */
  intakeCompleted: boolean;
  completeIntake: () => void;
  toggleChatUi: (isOpen?: boolean) => void;
  setLayout: (layout: ChatLayout) => void;
  send: (text: string, opts?: { isQuickStartButton?: boolean }) => void;
  sendVoice: (audioBlob: Blob) => void;
  /**
   * Most recent voice-message failure. The widget renders a dismissible
   * notice banner when set; calling `dismissVoiceError()` clears it.
   * `retryVoiceError()` re-runs the send pipeline with the cached blob.
   */
  voiceError: VoiceErrorState | null;
  dismissVoiceError: () => void;
  retryVoiceError: () => void;
  /**
   * True from the moment a voice send is kicked off until the transcription
   * request resolves (or fails). Used by `ChatInput` to keep the
   * "Transcribing your voice note…" pill on screen for exactly that window —
   * it must NOT include the bot-reply phase.
   */
  isTranscribingVoice: boolean;
  /** True when the voice recorder owns the input row (recording / review / transcribing). */
  isVoiceActive: boolean;
  setIsVoiceActive: (active: boolean) => void;
  /** True when the visitor has sent at least one message in the active session (across pages/tabs/cleared chats). */
  hasSentMessageInSession?: boolean;
  updateFeedback: (
    timestamp: number,
    rating: 'up' | 'down' | null,
    comment?: string
  ) => void;
  clearHistory: () => void;
  /** Ref to the floating “open chat” control for focus restoration when the panel closes */
  launcherRef: React.RefObject<HTMLButtonElement | null>;
}
const chatContext = React.createContext<ChatState | null>(null);

type WidgetCustomization = WidgetConfig['customization'];
type WidgetConfigContextValue = WidgetCustomization & {
  captureMclidCookie: boolean;
  viewerCountry: string | null;
};
const configContext = React.createContext<WidgetConfigContextValue | null>(
  null
);

const DESKTOP_WIDGET_DEFAULTS = { open: false, layout: 'docked' as const };

function getDesktopDefaults(dockModeEnabled: boolean) {
  if (dockModeEnabled) {
    return { open: true, layout: 'minimized' as const };
  }
  return DESKTOP_WIDGET_DEFAULTS;
}

type QueuedMessage = {
  text: string;
  userMessageTimestamp: number;
  messageId: string;
  isQuickStartButton: boolean;
  isAvatarMessage?: boolean;
};

export function ChatBotProvider(props: {
  children: React.ReactNode;
  engageId: string;
  customization: WidgetCustomization;
  /** ISO alpha-2 from chat-config, or null when CloudFront did not send a country. */
  viewerCountry?: string | null;
  captureMclidCookie?: boolean;
  mandatoryIntakeOnOpen?: boolean;
  initialServiceDown?: boolean; // True when initial config fetch failed
  dockModeEnabled?: boolean;
  /** Chat UI hosting this provider; sent with every message. Defaults to 'widget'. */
  chatSurface?: Extract<ChatSurface, 'widget' | 'embedded'>;
}) {
  const dockModeEnabled = props.dockModeEnabled ?? false;
  const mandatoryIntakeOnOpen = props.mandatoryIntakeOnOpen === true;
  // Track if device is mobile
  const [isMobile, setIsMobile] = React.useState<boolean>(getIsMobile());
  const desktopDefaults = React.useMemo(
    () => getDesktopDefaults(dockModeEnabled),
    [dockModeEnabled]
  );

  // On mobile: start closed (show floating button), on desktop: start closed with FAB
  // (or minimized bar when dock mode is enabled via embed script).
  // When service is initially down (config failed), always start closed.
  const [open, toggleChatUi] = React.useState<boolean>(
    props.initialServiceDown ? false : isMobile ? false : desktopDefaults.open
  );
  const [state, setStatus] = React.useState<ChatState['state']>('idle');
  const [layout, setLayoutState] = React.useState<ChatLayout>(
    isMobile ? 'docked' : desktopDefaults.layout
  );
  const setLayout = React.useCallback(
    (next: ChatLayout) => {
      if (!dockModeEnabled) return;
      setLayoutState(next);
    },
    [dockModeEnabled]
  );
  const [history, setHistory] = React.useState<ChatMessage[]>([]);
  const [queue, setQueue] = React.useState<QueuedMessage[]>([]);
  const [isProcessing, setIsProcessing] = React.useState<boolean>(false);
  const [isServiceDown, setIsServiceDown] = React.useState<boolean>(
    props.initialServiceDown ?? false
  );
  const [voiceError, setVoiceError] = React.useState<VoiceErrorState | null>(
    null
  );
  const [isTranscribingVoice, setIsTranscribingVoice] = React.useState(false);
  const [isVoiceActive, setIsVoiceActive] = React.useState(false);
  const avatarStreamActiveRef = React.useRef(false);
  const touchAvatarActivityRef = React.useRef<() => void>(() => {});

  function AvatarSessionBridge() {
    const { isAvatarStreamActive, touchAvatarActivity } = useAvatarSession();
    React.useEffect(() => {
      avatarStreamActiveRef.current = isAvatarStreamActive;
    }, [isAvatarStreamActive]);
    React.useEffect(() => {
      touchAvatarActivityRef.current = touchAvatarActivity;
    }, [touchAvatarActivity]);
    return null;
  }

  const notifyAvatarActivity = React.useCallback(() => {
    if (avatarStreamActiveRef.current) {
      touchAvatarActivityRef.current();
    }
  }, []);

  const { panelWidth, setPanelWidth } = usePersistedPanelWidth(
    CHAT_WIDGET_PANEL_WIDTH_KEY,
    PANEL_WIDTH_DEFAULT
  );

  const launcherRef = React.useRef<HTMLButtonElement | null>(null);
  const processingGenerationRef = React.useRef(0);
  const activeStreamReaderRef =
    React.useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);

  const [sessionId, setSessionId] = React.useState(() =>
    getOrSetSessionId(props.engageId)
  );
  const [threadId, setThreadId] = React.useState(() =>
    getOrSetThreadId(props.engageId)
  );
  const [hasSentMessageInSession, setHasSentMessageInSession] = React.useState(
    () => readLastMessageAt(props.engageId) !== null
  );
  const sessionIdRef = React.useRef(sessionId);
  const threadIdRef = React.useRef(threadId);
  sessionIdRef.current = sessionId;
  threadIdRef.current = threadId;

  const [intakeCompleted, setIntakeCompleted] = React.useState(() =>
    mandatoryIntakeOnOpen ? isIntakeCompleted(props.engageId, sessionId) : true
  );
  const completeIntake = React.useCallback(() => {
    markIntakeCompleted(props.engageId, sessionIdRef.current);
    setIntakeCompleted(true);
  }, [props.engageId]);

  // Latest ids snapshot for async telemetry that fires during the n8n send
  // pipeline. Kept current on every render so retry/streaming callbacks
  // always see the active session/thread without re-binding closures.
  const sessionIdentityRef = React.useRef({ sessionId, threadId });
  sessionIdentityRef.current = { sessionId, threadId };

  const resetStaleSessionIdentity = React.useCallback(() => {
    const newSessionId = nanoid();
    const newThreadId = nanoid();
    persistSessionId(props.engageId, newSessionId);
    persistThreadId(props.engageId, newThreadId);
    sessionIdRef.current = newSessionId;
    threadIdRef.current = newThreadId;
    setSessionId(newSessionId);
    setThreadId(newThreadId);
    trackWidgetEvent('Chat Widget - Session identity reset', {
      institutionId: props.engageId,
      sessionId: newSessionId,
      threadId: newThreadId,
      reason: 'stale_session_thread',
    });
  }, [props.engageId]);

  const mintNewThreadId = React.useCallback(() => {
    const newThreadId = nanoid();
    persistThreadId(props.engageId, newThreadId);
    threadIdRef.current = newThreadId;
    setThreadId(newThreadId);
    return newThreadId;
  }, [props.engageId]);

  const sessionTimeoutSetting = props.customization.sessionTimeout ?? '30d';

  const checkAndResetIfInactive = React.useCallback(
    (isWidgetOpen: boolean) => {
      const lastMessageAt = readLastMessageAt(props.engageId);
      const nowMs = Date.now();
      if (
        !isSessionInactiveExpired(lastMessageAt, sessionTimeoutSetting, nowMs)
      ) {
        setHasSentMessageInSession(lastMessageAt !== null);
        return false;
      }

      const previousSessionId = sessionIdRef.current;
      const newSessionId = nanoid();
      const newThreadId = nanoid();
      persistSessionId(props.engageId, newSessionId);
      persistThreadId(props.engageId, newThreadId);
      clearLastMessageAt(props.engageId);
      setHasSentMessageInSession(false);

      sessionIdRef.current = newSessionId;
      threadIdRef.current = newThreadId;
      sessionIdentityRef.current = {
        sessionId: newSessionId,
        threadId: newThreadId,
      };
      setSessionId(newSessionId);
      setThreadId(newThreadId);
      updateWidgetAnalyticsSessionId(newSessionId);

      processingGenerationRef.current += 1;
      activeStreamReaderRef.current?.cancel().catch(() => {});
      activeStreamReaderRef.current = null;
      setHistory([]);
      setQueue([]);
      setIsProcessing(false);
      setStatus('idle');
      setVoiceError(null);
      setIsTranscribingVoice(false);

      const nextIntakeDone = mandatoryIntakeOnOpenRef.current
        ? isIntakeCompleted(props.engageId, newSessionId)
        : true;
      intakeCompletedRef.current = nextIntakeDone;
      setIntakeCompleted(nextIntakeDone);

      trackWidgetEvent('Chat Widget - Session identity reset', {
        institutionId: props.engageId,
        sessionId: newSessionId,
        threadId: newThreadId,
        reason: 'inactivity_timeout',
        previousSessionId,
        timeoutSetting: sessionTimeoutSetting,
        inactiveFor: getInactiveForBucket(nowMs - (lastMessageAt ?? nowMs)),
        isWidgetOpen,
      });

      return true;
    },
    [props.engageId, sessionTimeoutSetting]
  );

  // Check inactivity timeout on page load and when timeout setting changes
  React.useEffect(() => {
    checkAndResetIfInactive(open);
    // Only run on mount / institution or timeout setting change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.engageId, sessionTimeoutSetting]);

  // Sync hasSentMessageInSession across tabs via storage events
  React.useEffect(() => {
    setHasSentMessageInSession(readLastMessageAt(props.engageId) !== null);
    const key = lastMessageAtStorageKey(props.engageId);
    const handleStorage = (event: StorageEvent) => {
      if (event.key === null || event.key === key) {
        setHasSentMessageInSession(readLastMessageAt(props.engageId) !== null);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [props.engageId]);

  // Max retries before showing service unavailable
  const MAX_RETRIES = 3;

  // Listen for resize events to update mobile state
  React.useEffect(() => {
    const handleResize = () => {
      const newIsMobile = getIsMobile();
      if (newIsMobile !== isMobile) {
        setIsMobile(newIsMobile);
        if (newIsMobile) {
          toggleChatUi(false);
        } else if (dockModeEnabled) {
          toggleChatUi(props.initialServiceDown ? false : desktopDefaults.open);
          setLayoutState(desktopDefaults.layout);
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [
    dockModeEnabled,
    desktopDefaults.layout,
    desktopDefaults.open,
    isMobile,
    props.initialServiceDown,
  ]);

  const intakeCompletedRef = React.useRef(intakeCompleted);
  intakeCompletedRef.current = intakeCompleted;
  const mandatoryIntakeOnOpenRef = React.useRef(mandatoryIntakeOnOpen);
  mandatoryIntakeOnOpenRef.current = mandatoryIntakeOnOpen;

  const send = React.useCallback(
    (
      text: string,
      opts?: { isQuickStartButton?: boolean; isAvatarMessage?: boolean }
    ) => {
      // Expand first so IntakeFormCard can render; then block send until intake completes.
      if (dockModeEnabled && layout === 'minimized') {
        setLayoutState('docked');
      }
      checkAndResetIfInactive(open);
      if (mandatoryIntakeOnOpenRef.current && !intakeCompletedRef.current)
        return;

      // Generate messageId in frontend for tracking across the entire chain
      const messageId = nanoid(21);
      const isQuickStartButton = opts?.isQuickStartButton ?? false;
      const isAvatarMessage = opts?.isAvatarMessage ?? false;
      const now = Date.now();
      writeLastMessageAt(props.engageId, now);
      setHasSentMessageInSession(true);

      // Add user message to history and queue for processing
      const userMsg: ChatMessage = {
        role: 'user',
        text: text,
        timestamp: now,
        messageId, // Store messageId with user message
      };

      // Add to queue for processing (use timestamp as unique ID)
      setQueue(q => [
        ...q,
        {
          text,
          userMessageTimestamp: userMsg.timestamp,
          messageId,
          isQuickStartButton,
          isAvatarMessage,
        },
      ]);
      setHistory(prev => [...prev, userMsg]);

      trackWidgetEvent('Chat Widget - User message sent', {
        institutionId: props.engageId,
        sessionId: sessionIdRef.current,
        threadId: threadIdRef.current,
        messageId,
        messageLength: text.length,
        messageSentAt: userMsg.timestamp,
      });
      notifyAvatarActivity();
    },
    [
      checkAndResetIfInactive,
      dockModeEnabled,
      layout,
      open,
      props.engageId,
      notifyAvatarActivity,
    ]
  );

  const performVoiceSend = React.useCallback(
    async (audioBlob: Blob, cachedUpload?: VoiceErrorState['uploaded']) => {
      if (dockModeEnabled && layout === 'minimized') {
        setLayoutState('docked');
      }
      checkAndResetIfInactive(open);
      if (mandatoryIntakeOnOpenRef.current && !intakeCompletedRef.current)
        return;

      // Set synchronously (before any await) so the `transcribing` recorder
      // state and this flag arrive in the same render. ChatInput keys the
      // TranscribingPill off this exact flag; if it's not yet true when
      // ChatInput's effect runs, the pill would flicker out instantly.
      //
      // The pill is the ONLY in-flight UX for voice messages — we
      // intentionally do NOT push an optimistic user bubble here. Adding a
      // bubble with placeholder copy ("🎤 Transcribing message…") would
      // misrepresent it as something the user said, then visibly swap to
      // their actual transcript. Instead we wait for the transcript and
      // push a single, final bubble with the real text.
      setIsTranscribingVoice(true);

      const messageId = nanoid(21);

      const apiEndpoint = import.meta.env.VITE_CHAT_API_ENDPOINT ?? '';

      // Step 1: upload to S3 (skipped if a previous attempt already uploaded
      // this blob and we're now retrying the transcribe step only).
      let audioUrl: string;
      let mediaKey: string;
      if (cachedUpload) {
        audioUrl = cachedUpload.audioUrl;
        mediaKey = cachedUpload.mediaKey;
      } else {
        trackWidgetEvent('Chat Widget - Voice message upload started', {
          institutionId: props.engageId,
          sessionId,
          threadId,
          messageId,
          audioSize: audioBlob.size,
          audioMimeType: audioBlob.type,
        });

        try {
          const result = await uploadVoiceRecording({
            apiEndpoint,
            blob: audioBlob,
          });
          audioUrl = result.downloadUrl;
          mediaKey = result.mediaKey;
        } catch (err) {
          const reason =
            err instanceof VoiceUploadError ? err.reason : 'unknown';
          trackWidgetEvent('Chat Widget - Voice message upload failed', {
            institutionId: props.engageId,
            sessionId,
            threadId,
            messageId,
            reason,
          });
          // No bubble was added to history yet, so nothing to clean up — the
          // error banner alone surfaces the failure, and the blob is cached
          // on `voiceError` so the user doesn't have to re-record.
          setVoiceError({ reason: 'upload_failed', blob: audioBlob });
          setIsTranscribingVoice(false);
          return;
        }
      }

      // Step 2: transcribe. n8n runs OpenAI Whisper (batch) and returns the
      // full transcript as a single JSON body. The TranscribingPill in the
      // input row carries the spinner / elapsed counter UX during the wait.
      let transcript: string;
      try {
        const result = await transcribeVoiceRecording({
          apiEndpoint,
          audioUrl,
          mediaKey,
          messageId,
          threadId,
          sessionId,
          institutionId: props.engageId,
        });
        transcript = result.transcript;
      } catch (err) {
        const reason =
          err instanceof VoiceTranscribeError
            ? err.reason
            : 'transcribe_failed';
        trackWidgetEvent('Chat Widget - Voice message transcription failed', {
          institutionId: props.engageId,
          sessionId,
          threadId,
          messageId,
          reason,
          mediaKey,
        });
        // No bubble to remove; surface the error banner. We retain the
        // upload result on `voiceError` so the retry button can skip
        // straight to the transcribe step.
        setVoiceError({
          reason: 'transcribe_failed',
          blob: audioBlob,
          uploaded: { audioUrl, mediaKey },
        });
        setIsTranscribingVoice(false);
        return;
      }

      // Step 3: now that we have the actual transcript, append the user
      // bubble to history. Timestamp is captured at this moment so the
      // bubble's ordering / feedback key reflects when it actually appears
      // in the chat.
      const userMessageTimestamp = Date.now();
      writeLastMessageAt(props.engageId, userMessageTimestamp);
      setHasSentMessageInSession(true);
      const userMsg: ChatMessage = {
        role: 'user',
        text: transcript,
        timestamp: userMessageTimestamp,
        messageId,
      };
      setHistory(prev => [...prev, userMsg]);

      trackWidgetEvent('Chat Widget - Voice message sent', {
        institutionId: props.engageId,
        sessionId: sessionIdRef.current,
        threadId: threadIdRef.current,
        messageId,
        audioSize: audioBlob.size,
        audioMimeType: audioBlob.type,
        mediaKey,
        transcriptLength: transcript.length,
        messageSentAt: userMessageTimestamp,
      });

      // Drop the "Transcribing…" pill now that the bubble is on screen; the
      // bot-reply phase is signalled separately via the standard
      // `state === 'pending'` path (spinner + disabled send).
      setIsTranscribingVoice(false);

      // Step 4: enqueue the transcript as a normal text chat message. From
      // this point on the queue processor treats it identically to a typed
      // message — no audioUrl, no transcript token plumbing.
      setQueue(q => [
        ...q,
        {
          text: transcript,
          userMessageTimestamp,
          messageId,
          isQuickStartButton: false,
        },
      ]);
      notifyAvatarActivity();
    },
    [
      checkAndResetIfInactive,
      dockModeEnabled,
      layout,
      open,
      props.engageId,
      sessionId,
      threadId,
      notifyAvatarActivity,
    ]
  );

  const sendVoice = React.useCallback(
    (audioBlob: Blob) => {
      // Clear any prior error before kicking off a fresh attempt; if this
      // attempt also fails, performVoiceSend will set a new voiceError.
      setVoiceError(null);
      void performVoiceSend(audioBlob);
    },
    [performVoiceSend]
  );

  const dismissVoiceError = React.useCallback(() => {
    setVoiceError(null);
  }, []);

  const retryVoiceError = React.useCallback(() => {
    // Read the cached error first, THEN mutate state and fire the side
    // effect — never touch performVoiceSend from inside the setVoiceError
    // updater. React may invoke updater functions more than once
    // (StrictMode dev double-invocation, concurrent-render replay) which
    // would cause duplicate uploads/transcribe requests if the side
    // effect lived inside the updater.
    if (!voiceError) return;
    const { blob, uploaded } = voiceError;
    setVoiceError(null);
    // If the previous attempt successfully uploaded but failed to
    // transcribe, replay only the transcribe step using the cached upload
    // result. Otherwise replay from upload.
    void performVoiceSend(blob, uploaded);
  }, [voiceError, performVoiceSend]);

  // Update feedback (thumbs up/down + optional comment) for a specific bot message
  const updateFeedback = React.useCallback(
    (timestamp: number, rating: 'up' | 'down' | null, comment?: string) => {
      setHistory(prev =>
        prev.map(msg =>
          msg.role === 'bot' && msg.timestamp === timestamp
            ? { ...msg, feedback: { rating, comment } }
            : msg
        )
      );
    },
    []
  );

  const clearHistory = React.useCallback(() => {
    processingGenerationRef.current += 1;
    const previousThreadId = threadIdRef.current;
    activeStreamReaderRef.current?.cancel().catch(() => {});
    activeStreamReaderRef.current = null;
    const newThreadId = mintNewThreadId();
    setHistory([]);
    setQueue([]);
    setIsProcessing(false);
    setStatus('idle');
    setVoiceError(null);
    setIsTranscribingVoice(false);
    trackWidgetEvent('Chat Widget - Conversation Cleared', {
      institutionId: props.engageId,
      sessionId: sessionIdRef.current,
      threadId: newThreadId,
      previousThreadId,
    });
  }, [mintNewThreadId, props.engageId]);

  // Process queued messages - batches all queued messages together
  React.useEffect(() => {
    if (isProcessing || queue.length === 0) return;

    const processNextBatch = async () => {
      const batchGeneration = processingGenerationRef.current;
      const isCancelled = () =>
        batchGeneration !== processingGenerationRef.current;
      const safeSetHistory = (updater: React.SetStateAction<ChatMessage[]>) => {
        if (isCancelled()) return;
        setHistory(updater);
      };

      setIsProcessing(true);
      setStatus('pending');
      notifyAvatarActivity();

      // Take snapshot of current queue to process
      const batch = [...queue];
      let botMessageIndex = -1;

      // Clear queue immediately (new messages sent during processing will form new queue)
      setQueue([]);

      const concatenatedText = batch.map(msg => msg.text).join('\n\n');
      const lastMsg = batch[batch.length - 1];
      const messageId = lastMsg.messageId;
      const messageSentAt = lastMsg.userMessageTimestamp;
      // Treat batch as quick-start only when every entry was a button click; a mixed batch falls back to false.
      const batchIsQuickStartButton = batch.every(
        msg => msg.isQuickStartButton
      );
      const batchIsAvatarMessage = batch.every(msg => msg.isAvatarMessage);
      // Add empty bot message after the LAST user message in the batch
      safeSetHistory(prev => {
        const botMsg: ChatMessage = {
          role: 'bot',
          text: '',
          timestamp: Date.now(),
        };
        const next = [...prev];

        // Find the last message in the batch by timestamp
        const lastMessageTimestamp =
          batch[batch.length - 1].userMessageTimestamp;
        const lastUserMsgIndex = next.findIndex(
          msg => msg.role === 'user' && msg.timestamp === lastMessageTimestamp
        );

        if (lastUserMsgIndex === -1) return next; // Safety check

        // Insert bot message right after the last user message
        const insertIndex = lastUserMsgIndex + 1;
        next.splice(insertIndex, 0, botMsg);
        botMessageIndex = insertIndex;
        return next;
      });

      // Get client context for EVERY message (backend uses merge semantics)
      // Server-side context (IP, country, headers) is captured by the BFF
      // Client-side context (pageUrl, timezone, deviceType) is captured here
      const clientContext = getClientContext();

      // Retry logic: try up to MAX_RETRIES times for the same message
      let attempt = 0;
      let success = false;
      let responseStatus: number | undefined;
      let accumulatedBotText = '';

      // Stream avatar speech incrementally during the LLM reply. Snapshot
      // avatarMode at send time so a mid-stream toggle doesn't flip behavior.
      const speakReplyOnAvatar = avatarStreamActiveRef.current;
      let speechOrchestrator: AvatarSpeechOrchestrator | null = null;

      const createSpeechOrchestrator = (): AvatarSpeechOrchestrator | null => {
        if (!speakReplyOnAvatar) return null;
        return createAvatarSpeechOrchestrator({
          messageId,
          messageSentAt,
          institutionId: props.engageId,
          sessionId: sessionIdentityRef.current.sessionId,
          track: trackWidgetEvent,
        });
      };

      while (attempt < MAX_RETRIES && !success) {
        attempt++;
        responseStatus = undefined;
        accumulatedBotText = '';
        if (speechOrchestrator) {
          await speechOrchestrator.abort();
        }
        speechOrchestrator = createSpeechOrchestrator();
        let firstChunkAt: number | undefined;
        let hasTrackedFirstChunk = false;

        const trackFirstChunk = () => {
          if (hasTrackedFirstChunk) return;

          firstChunkAt = Date.now();
          hasTrackedFirstChunk = true;

          trackWidgetEvent('Chat Widget - Bot reply started', {
            institutionId: props.engageId,
            sessionId: sessionIdentityRef.current.sessionId,
            threadId: sessionIdentityRef.current.threadId,
            messageId,
            messageSentAt,
            firstChunkAt,
            timeToFirstChunkMs: firstChunkAt - messageSentAt,
          });
        };

        try {
          const { reader, status } = await sendMessage({
            text: concatenatedText,
            engageId: props.engageId,
            sessionId: sessionIdRef.current,
            threadId: threadIdRef.current,
            clientContext,
            messageId,
            isQuickStartButton: batchIsQuickStartButton,
            isAvatarMessage: batchIsAvatarMessage,
            captureMclidCookie: props.captureMclidCookie === true,
            chatSurface: props.chatSurface ?? 'widget',
          });
          responseStatus = status;

          const decoder = new TextDecoder();
          if (!reader) throw new Error('No response body');

          activeStreamReaderRef.current = reader;

          // Buffer for incomplete lines that span multiple chunks
          let buffer = '';

          try {
            while (true) {
              if (isCancelled()) break;
              const { value, done: streamDone } = await reader.read();
              if (streamDone) break;
              if (!value) continue;

              const raw = decoder.decode(value, { stream: true });
              buffer += raw;

              const lines = buffer.split('\n');
              buffer = lines.pop() ?? '';

              let appendText = '';
              for (const line of lines) {
                const parsed = processLine(line);
                if (parsed.type === 'continue') {
                  console.error('[Unexpected incomplete line]', line);
                  continue;
                }
                if (
                  parsed.type === 'text' ||
                  parsed.type === 'error' ||
                  parsed.type === 'done'
                ) {
                  appendText += parsed.content ?? '';
                }
              }

              if (appendText) {
                trackFirstChunk();
                notifyAvatarActivity();
                accumulatedBotText += appendText;
                if (speechOrchestrator) {
                  try {
                    await speechOrchestrator.appendRawText(appendText);
                  } catch (e) {
                    console.warn('[ChatBot] avatar speech append failed', e);
                  }
                }
                safeSetHistory(prev => {
                  const next = [...prev];
                  if (
                    botMessageIndex >= 0 &&
                    botMessageIndex < next.length &&
                    next[botMessageIndex]
                  ) {
                    next[botMessageIndex] = {
                      ...next[botMessageIndex],
                      role: 'bot',
                      timestamp: Date.now(),
                      text: (next[botMessageIndex].text ?? '') + appendText,
                    } as ChatMessage;
                  }
                  return next;
                });
              }
            }

            if (isCancelled()) return;

            // Process any remaining data in buffer after stream ends
            if (buffer.trim()) {
              const parsed = processLine(buffer);
              const finalText =
                parsed.type === 'text' ||
                parsed.type === 'error' ||
                parsed.type === 'done'
                  ? (parsed.content ?? '')
                  : '';

              if (finalText) {
                accumulatedBotText += finalText;
                notifyAvatarActivity();
                if (speechOrchestrator) {
                  try {
                    await speechOrchestrator.appendRawText(finalText);
                  } catch (e) {
                    console.warn('[ChatBot] avatar speech append failed', e);
                  }
                }
                safeSetHistory(prev => {
                  const next = [...prev];
                  if (
                    botMessageIndex >= 0 &&
                    botMessageIndex < next.length &&
                    next[botMessageIndex]
                  ) {
                    next[botMessageIndex] = {
                      ...next[botMessageIndex],
                      role: 'bot',
                      timestamp: Date.now(),
                      text: (next[botMessageIndex].text ?? '') + finalText,
                    } as ChatMessage;
                  }
                  return next;
                });
              }
            }

            // N8N / stream-transformer signals a stale session/thread with a
            // literal "Not found" item chunk — mint fresh ids and retry once
            // without counting against MAX_RETRIES.
            if (isStaleSessionNotFoundResponse(accumulatedBotText)) {
              resetStaleSessionIdentity();
              accumulatedBotText = '';
              if (speechOrchestrator) {
                await speechOrchestrator.abort();
              }
              speechOrchestrator = null;
              safeSetHistory(prev => {
                const next = [...prev];
                if (
                  botMessageIndex >= 0 &&
                  botMessageIndex < next.length &&
                  next[botMessageIndex]
                ) {
                  next[botMessageIndex] = {
                    ...next[botMessageIndex],
                    role: 'bot',
                    timestamp: Date.now(),
                    text: '',
                  } as ChatMessage;
                }
                return next;
              });
              attempt--;
              continue;
            }

            // Success! Exit retry loop
            success = true;
            const completedAt = Date.now();
            writeLastMessageAt(props.engageId, completedAt);

            trackWidgetEvent('Chat Widget - Bot reply received', {
              institutionId: props.engageId,
              sessionId,
              threadId,
              messageId,
              responseLength: accumulatedBotText.length,
              messageSentAt,
              completedAt,
              totalResponseTimeMs: completedAt - messageSentAt,
              ...(firstChunkAt && {
                firstChunkAt,
                timeToFirstChunkMs: firstChunkAt - messageSentAt,
                streamCompletionMs: completedAt - firstChunkAt,
              }),
            });
          } finally {
            if (activeStreamReaderRef.current === reader) {
              activeStreamReaderRef.current = null;
            }
          }
        } catch (err) {
          console.error(
            `[ChatBot] Message send failed (attempt ${attempt}/${MAX_RETRIES}):`,
            err
          );

          if (responseStatus !== undefined) {
            // Stream read failure: sendMessage succeeded (got HTTP response) but stream broke
            trackWidgetEvent('Chat Widget - Message Stream Read Failed', {
              threadId: threadIdRef.current,
              messageId,
              httpStatus: responseStatus,
              partialResponseLength: accumulatedBotText.length,
              errorType: err instanceof Error ? err.name : 'Unknown',
            });
          } else if (attempt < MAX_RETRIES) {
            // Send failure with remaining retries: sendMessage itself threw (network/HTTP error)
            trackWidgetEvent('Chat Widget - Message Send Retry', {
              attempt,
              maxRetries: MAX_RETRIES,
              threadId: threadIdRef.current,
              messageId,
              errorType: err instanceof Error ? err.name : 'Unknown',
            });
          }

          if (attempt < MAX_RETRIES) {
            await new Promise(resolve => setTimeout(resolve, attempt * 5000));
          }
        }
      }

      if (speechOrchestrator) {
        try {
          if (success) {
            await speechOrchestrator.complete();
          } else {
            await speechOrchestrator.fail();
          }
        } catch (e) {
          console.warn('[ChatBot] avatar speech finalize failed', e);
        } finally {
          speechOrchestrator.dispose();
        }
      }

      // If all retries failed, mark service as down and show error.
      // Voice messages no longer take a special path here: they're
      // transcribed BEFORE being enqueued, so a queue-level failure means
      // the chat reply itself failed and the user should see the same
      // service-unavailable treatment as for any text message.
      if (!success) {
        console.error(
          `[ChatBot] All ${MAX_RETRIES} attempts failed - marking service as down`
        );
        trackWidgetEvent('Chat Widget - Message Send Failed', {
          totalAttempts: MAX_RETRIES,
          threadId,
          messageId,
        });

        if (!isCancelled()) {
          setIsServiceDown(true);
          safeSetHistory(prev => {
            const next = [...prev];
            if (
              botMessageIndex >= 0 &&
              botMessageIndex < next.length &&
              next[botMessageIndex]
            ) {
              next[botMessageIndex] = {
                ...next[botMessageIndex],
                role: 'bot',
                timestamp: Date.now(),
                text:
                  props.customization.serviceUnavailableMessage ||
                  'Service is currently unavailable. Please try again later.',
              } as ChatMessage;
            }
            return next;
          });
        }
      }

      if (isCancelled()) return;

      // Mark processing complete
      setIsProcessing(false);
      setStatus('idle');
    };

    processNextBatch();
  }, [
    queue,
    isProcessing,
    sessionId,
    threadId,
    props.engageId,
    props.customization.serviceUnavailableMessage,
    resetStaleSessionIdentity,
    notifyAvatarActivity,
  ]);

  // Avatar push-to-talk: when the user releases the mic button on the
  // Anam avatar, the persona-side STT emits a finalized transcript which
  // arrives here via MESSAGE_HISTORY_UPDATED. We feed it into the same
  // n8n send path as typed input so the existing queue/retry/Anam-TTS
  // pipeline handles the reply identically. Subscribed once for the
  // provider's lifetime — anamClient dedupes repeats internally.
  React.useEffect(() => {
    return anamClient.onUserTranscript(text => {
      const trimmed = text.trim();
      if (!trimmed) return;
      send(trimmed, { isAvatarMessage: true });
    });
    // `send` identity depends on layout/engageId/sessionId/threadId; re-
    // subscribing across those changes is harmless and keeps the closure
    // pinned to the latest send.
  }, [send]);

  return (
    <AvatarSessionProvider
      institutionId={props.engageId}
      institutionName={props.customization.institutionName}
      sessionId={sessionId}
      threadId={threadId}
      photorealAvatarEnabled={
        props.customization.photorealAvatarEnabled ?? false
      }
      avatarMaxSessionLengthSeconds={
        props.customization.avatarMaxSessionLengthSeconds
      }
    >
      <AvatarSessionBridge />
      <configContext.Provider
        value={{
          ...props.customization,
          captureMclidCookie: props.captureMclidCookie === true,
          viewerCountry: props.viewerCountry ?? null,
        }}
      >
        <chatContext.Provider
          value={{
            layout,
            dockModeEnabled,
            setLayout,
            panelWidth,
            setPanelWidth,
            send,
            sendVoice,
            voiceError,
            dismissVoiceError,
            retryVoiceError,
            isTranscribingVoice,
            isVoiceActive,
            setIsVoiceActive,
            hasSentMessageInSession,
            history,
            threadId,
            sessionId,
            engageId: props.engageId,
            mandatoryIntakeOnOpen,
            intakeCompleted,
            completeIntake,
            state,
            open,
            isServiceDown,
            updateFeedback,
            clearHistory,
            launcherRef,
            toggleChatUi: (isOpen?: boolean) => {
              const shouldOpen = isOpen !== undefined ? isOpen : !open;

              if (shouldOpen && !open) {
                checkAndResetIfInactive(false);
                if (dockModeEnabled && layout === 'minimized') {
                  setLayoutState('docked');
                }
              }

              toggleChatUi(shouldOpen);
            },
          }}
        >
          {props.children}
        </chatContext.Provider>
      </configContext.Provider>
    </AvatarSessionProvider>
  );
}

export function useChatBot() {
  const context = React.useContext(chatContext);
  if (!context)
    throw new Error('useChatBot must be used within a ChatProvider');
  return context;
}

export function useGetConfig() {
  const context = React.useContext(configContext);
  if (!context)
    throw new Error('useGetConfig must be used within a ChatBotProvider');
  return context;
}

// API Call
// -------------------------------
async function sendMessage(payload: {
  text: string;
  engageId: string;
  sessionId: string;
  threadId: string;
  clientContext?: ClientContext;
  messageId: string;
  isQuickStartButton?: boolean;
  isAvatarMessage?: boolean;
  captureMclidCookie?: boolean;
  chatSurface: ChatSurface;
}): Promise<{
  reader: ReadableStreamDefaultReader<Uint8Array>;
  status: number;
}> {
  const {
    text,
    engageId,
    sessionId,
    threadId,
    clientContext,
    messageId,
    isQuickStartButton,
    isAvatarMessage,
    captureMclidCookie = false,
    chatSurface,
  } = payload;
  const url = `${import.meta.env.VITE_CHAT_API_ENDPOINT}/api/chat/v1/institution/${engageId}/sessions/${sessionId}/threads/${threadId}/message`;
  const cookieString = typeof document === 'undefined' ? '' : document.cookie;
  const metadata = mergeMclidMetadata(
    getMetadataSnapshot(),
    captureMclidCookie,
    cookieString
  );
  const hasMetadata = Boolean(metadata && Object.keys(metadata).length > 0);
  const widgetVersion = getWidgetVersion();

  const fetchInit: RequestInit = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      message: text,
      messageId,
      ...(clientContext?.pageUrl && { pageUrl: clientContext.pageUrl }),
      ...(clientContext?.timezone && { timezone: clientContext.timezone }),
      ...(clientContext?.deviceType && {
        deviceType: clientContext.deviceType,
      }),
      chatSurface,
      ...(isQuickStartButton && { isQuickStartButton: true }),
      ...(isAvatarMessage && { isAvatarMessage: true }),
      ...(hasMetadata && { metadata }),
      ...(widgetVersion && { widgetVersion }),
    }),
  };

  return await fetch(url, fetchInit).then(async res => {
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    if (!res.body) throw new Error('No response body');
    return { reader: res.body.getReader(), status: res.status };
  });
}
/**
 * Process a single line from the NDJSON stream.
 *
 * Input format (from API):
 * - { type: 'item', content: string } - Text content
 * - { type: 'end' } - End marker
 * - { type: 'error', content: string } - Error message
 *
 * Internal marker (not exposed to the widget UI):
 * - { type: 'begin' } - Start-of-stream marker that is mapped to 'ignore'
 *
 * Output (for widget state):
 * - type: 'text' - Display content
 * - type: 'done' - Stream finished
 * - type: 'error' - Show error
 * - type: 'ignore' - Skip (e.g. 'begin' marker or empty lines)
 * - type: 'continue' - JSON parse failed (incomplete data)
 */
function isStaleSessionNotFoundResponse(text: string): boolean {
  return text.trim() === 'Not found';
}

function processLine(line: string): {
  type: 'text' | 'done' | 'error' | 'ignore' | 'continue';
  content?: string;
} {
  if (!line.trim()) return { type: 'ignore' };

  try {
    const payload = JSON.parse(line) as {
      type: 'item' | 'begin' | 'end' | 'error';
      content?: string;
    };

    // Map NDJSON types to widget types using object lookup for readability
    const typeMap: Record<
      typeof payload.type,
      'text' | 'done' | 'error' | 'ignore'
    > = {
      item: 'text',
      begin: 'ignore',
      end: 'done',
      error: 'error',
    };

    return {
      type: typeMap[payload.type] ?? 'ignore',
      content: payload.content,
    };
  } catch (err) {
    // Use debug level - incomplete JSON is expected during streaming
    console.debug('[Debug] Error processing line', err);
    return { type: 'continue' };
  }
}
